/* @flow */


import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TablePagination from '@material-ui/core/TablePagination';
import Tooltip from '@material-ui/core/Tooltip';

import AmountFormatterComponent from 'components/formatters/amount';
import ButtonComponent from 'components/buttons/button';
import DateFormatterComponent from 'components/formatters/date';
import IconEdit from 'components/icons/edit';
import IconTrash from 'components/icons/trash';
import IntlMessageComponent from 'components/formatters/intlMessage';
import InvoiceNameComponent from 'components/invoice/name';
import PageSubTitleViewComponent from 'components/pageTitle/subTitle';
import SupplierInvoiceStatusViewComponent from 'components/invoiceStatus/seller';
import StyledTableCellTextComponent from 'components/table/cellText';
import StyledTableCellNumbersComponent from 'components/table/cellNumbers';
import StyledTableHeadComponent from 'components/table/head';
import StyledTableRowComponent from 'components/table/row';
import StyledTableRowSupplierInvoiceComponent from 'components/table/rowSellerInvoice';
import SecurityComponent from 'components/security/index';

import Invoice from 'models/invoices/seller/invoice';

import {
  INVOICE_SELLER_TYPE_PUBLISHED,
  INVOICE_SELLER_TYPE_PAY_FEES,
  INVOICE_SELLER_TYPE_FOLLOWUP,
  INVOICE_SELLER_TYPE_SIGN_DEED,
  INVOICE_SELLER_TYPE_REFUSED
} from 'constants/invoiceTypes';

import {
  PROTECTED_SELLER_INVOICE_DETAILS,
  PROTECTED_SELLER_SELL,
  PROTECTED_SUPPLIER_INVOICE_DETAILS
} from 'constants/pageRoutes';
import TooltipComponent from 'components/toolTips/index';


const columnDataSupplier = [
  { id: 'type', align: 'left', translationKey: 'component.seller.invoiceList.header.status' },
  { id: 'id', align: 'left', translationKey: 'component.seller.invoiceList.header.invoiceNbr' },
  { id: 'reference', align: 'left', translationKey: 'component.seller.invoiceList.header.reference' },
  { id: 'debtorName', align: 'left', translationKey: 'component.seller.invoiceList.header.debtor' },
  { id: 'debtAmount', translationKey: 'component.seller.invoiceList.header.amount' },
  { id: 'financedAmount', translationKey: 'component.seller.invoiceList.header.financedAmount' },
  { id: 'cashToTransfer', translationKey: 'component.seller.invoiceList.header.cashToTransfer' },
  { id: 'financedDate', align: 'left', translationKey: 'component.supplier.invoiceList.header.financedDate' },
  { id: 'dueDate', align: 'left', translationKey: 'component.seller.invoiceList.header.dueDate' }
];

type SupplierInvoiceListViewComponentProps = {
  invoices: Invoice[];
  order: string;
  orderBy: string;
  page: number;
  pageSize: number;
  isSupplier: boolean;
  cancelInvoice: (id: number) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleSortOrderChange: (property: string) => void;
  payFees: (ids: number[]) => void;
  paySelectedFees: () => void;
  selectInvoice: (id: number) => void;
};

export default function SupplierInvoiceListViewComponent(props: SupplierInvoiceListViewComponentProps) {
  const history = useHistory();
  const canExecute = useSelector(state => state.auth.user.activeCompany.roleSupplier.canExecute);
  const columnDataToUse = columnDataSupplier;

  const handlePageChange = (_, page) => props.handlePageChange(page);
  const handlePageSizeChange = event => props.handlePageSizeChange(event.target.value);

  const isActionRequired = (id: number) => {
    switch (id) {
      case INVOICE_SELLER_TYPE_PUBLISHED:
      case INVOICE_SELLER_TYPE_PAY_FEES:
      case INVOICE_SELLER_TYPE_FOLLOWUP:
      case INVOICE_SELLER_TYPE_SIGN_DEED:
        return true;
      default:
        return false;
    }
  };

  const onClickRow = (invoiceId: number, type: number) => {
    history.push(`${PROTECTED_SUPPLIER_INVOICE_DETAILS}/${invoiceId}`);
  };


  return (
    <React.Fragment>
      <PageSubTitleViewComponent id="component.seller.invoiceList.title" />

      <Box mt={3}>
        <Box component="div" overflow="auto">
          <Table>
            <StyledTableHeadComponent>
              <StyledTableRowComponent>
                <StyledTableCellTextComponent padding="checkbox" />

                {columnDataToUse.map(column => (
                  <React.Fragment>
                    <StyledTableCellTextComponent align={column?.align ?? 'right'} key={column.id} padding="none" pageName="Tooltip.Seller.InvoiceColumntTitle" elementName={column.id}>
                      <TooltipComponent pageName="Tooltip.Seller.InvoiceColumntTitle" elementName={column.id}>
                        <TableSortLabel
                          active={props.orderBy === column.id}
                          direction={props.order}
                          onClick={() => props.handleSortOrderChange(column.id)}
                        ><IntlMessageComponent id={column.translationKey} /></TableSortLabel>
                      </TooltipComponent>
                    </StyledTableCellTextComponent>
                  </React.Fragment>
                ))}

                <StyledTableCellTextComponent padding="none" />
              </StyledTableRowComponent>
            </StyledTableHeadComponent>

            <TableBody bgcolor="white">
              {props.invoices.length === 0 &&
                <StyledTableRowComponent>
                  <StyledTableCellTextComponent colSpan={columnDataToUse.length + 2}>
                    <Box textAlign="center">
                      <IntlMessageComponent id="seller.invoices.noData" />
                    </Box>
                  </StyledTableCellTextComponent>
                </StyledTableRowComponent>
              }

              {props.invoices.slice(props.page * props.pageSize, (props.page * props.pageSize) + props.pageSize).map(i => (
                <StyledTableRowSupplierInvoiceComponent
                  key={`invList-${i.id}`}
                  hover
                  onClick={canExecute ? () => onClickRow(i.id, i.type) : null}
                  isRefused={i.type === INVOICE_SELLER_TYPE_REFUSED}
                  isWaiting={isActionRequired(i.type)}
                >
                  <StyledTableCellTextComponent padding="checkbox" />

                  <StyledTableCellTextComponent padding="none">
                    <SupplierInvoiceStatusViewComponent invoiceType={i.type} />
                  </StyledTableCellTextComponent>

                  <StyledTableCellTextComponent padding="none">
                    <InvoiceNameComponent invoiceId={i.id} invoiceName={i.invoiceName} />
                  </StyledTableCellTextComponent>

                  <StyledTableCellTextComponent padding="none">
                    {i.reference}
                  </StyledTableCellTextComponent>

                  <StyledTableCellTextComponent padding="none">
                    <b>{i.debtorName}</b>
                  </StyledTableCellTextComponent>

                  <StyledTableCellNumbersComponent padding="none">
                    <AmountFormatterComponent amount={i.debtAmount} />
                  </StyledTableCellNumbersComponent>

                  <StyledTableCellNumbersComponent padding="none">
                    <AmountFormatterComponent amount={i.financedAmount} />
                  </StyledTableCellNumbersComponent>

                  <StyledTableCellNumbersComponent padding="none">
                    <AmountFormatterComponent amount={i.cashToTransfer} notApplicableIfMissing />
                  </StyledTableCellNumbersComponent>

                  <StyledTableCellTextComponent padding="none">
                    <DateFormatterComponent date={i.estimateDate} />
                  </StyledTableCellTextComponent>

                  <StyledTableCellTextComponent padding="none">
                    <DateFormatterComponent date={i.dueDate} />
                  </StyledTableCellTextComponent>

                  <StyledTableCellTextComponent padding="none">
                  </StyledTableCellTextComponent>
                </StyledTableRowSupplierInvoiceComponent>
              ))}
            </TableBody>
          </Table>

        </Box>

        <Box display="flex" height={60}>
          <Box flexGrow={1} mt={2}>
          </Box>
          <Box justifyContent="flex-end">
            <TablePagination
              component="div"
              count={props.invoices.length}
              rowsPerPage={props.pageSize}
              page={props.page}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePageSizeChange}
              labelRowsPerPage={<IntlMessageComponent id="general.invoicePerPage" />}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          </Box>
        </Box>
      </Box>
    </React.Fragment >
  );
}
